import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Slider } from "./ui/slider";
import { scoreSurvey } from "../lib/survey";

export default function SurveyEngine({ title, subtitle, questions, onComplete }) {
  const [i, setI] = useState(0);
  const [answers, setAnswers] = useState({});

  const q = questions[i];
  const value = answers[q.id];

  // Pre-seed neutral mid value when entering a question. User can adjust freely;
  // this avoids the "Next disabled" stall when sliders have no initial click.
  useEffect(() => {
    if (answers[q.id] === undefined) {
      setAnswers((a) => ({ ...a, [q.id]: 3 }));
    }
  }, [q.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function setAnswer(v) {
    setAnswers((a) => ({ ...a, [q.id]: v }));
  }

  function next() {
    if (i < questions.length - 1) setI(i + 1);
    else {
      // Use the normalized scoring engine so every question contributes
      // consistently regardless of positive/negative wording.
      const summary = scoreSurvey(questions, answers);
      const payload = questions.map((qq) => ({
        question_id: qq.id,
        text: qq.text,
        category: qq.category,
        reverse: !!qq.reverse,
        value: answers[qq.id],
      }));
      onComplete({
        answers: payload,
        score: summary.normalized,       // 0..100 normalized total (back-compat)
        raw_score: summary.raw_total,    // sum of raw 1..5
        normalized_score: summary.normalized,
        categories: summary.categories,
      });
    }
  }

  const progress = ((i + 1) / questions.length) * 100;

  return (
    <div className="space-y-8" data-testid="survey-engine">
      <div>
        <div className="bps-eyebrow mb-2">Survey · {i + 1} of {questions.length}</div>
        <h2 className="font-display text-2xl font-medium tracking-tight">{title}</h2>
        <p className="text-sm text-[#9AAEC6] mt-1">{subtitle}</p>
      </div>

      <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-[#22D3EE]"
          initial={false}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={q.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.4 }}
          className="bps-card p-6 space-y-6"
        >
          <p className="font-display text-xl text-white leading-snug">{q.text}</p>

          {q.type === "scale" && (
            <div className="space-y-4">
              <Slider
                value={[value || 3]}
                onValueChange={(v) => setAnswer(v[0])}
                min={1}
                max={5}
                step={1}
                data-testid="scale-slider"
              />
              <div className="flex justify-between text-xs text-[#7C8FA8]">
                {q.options.map((o) => (
                  <button
                    type="button"
                    key={o.value}
                    onClick={() => setAnswer(o.value)}
                    data-testid={`scale-option-${o.value}`}
                    className={`px-1 transition-all ${value === o.value ? "text-[#22D3EE] font-semibold" : "hover:text-white/80"}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="text-center text-[11px] uppercase tracking-[0.2em] text-[#7C8FA8]">
                {value ? q.options.find((o) => o.value === value)?.label : "Slide or tap to respond"}
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      <div className="flex items-center gap-3">
        {i > 0 && (
          <button
            onClick={() => setI(i - 1)}
            className="flex-1 py-4 rounded-full bg-white/5 text-white font-medium"
            data-testid="survey-back"
          >
            Back
          </button>
        )}
        <button
          onClick={next}
          disabled={!value}
          data-testid="survey-next"
          className="flex-1 py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-medium disabled:opacity-30 transition-all hover:-translate-y-0.5"
        >
          {i === questions.length - 1 ? "Complete" : "Next"}
        </button>
      </div>
    </div>
  );
}
